import {
  TipoEmpleadoEnumChoice,
  TipoIdentificacionEnumChoice,
  gridSizeMdLg4,
  newEmpleadoschema,
} from "@/shared";
import {
  useFetchAllCountries,
  useFetchAreas,
  useFetchCanalVentaAll,
  useFetchCitiesEnabled,
  useFetchDepartamentos,
  useFetchProvinces,
  useFetchSearchIdentification,
  useFetchZones,
  usefetchEmpresas,
} from "@/shared/actions/erp";
import {
  CustomAutocomplete,
  CustomTextField,
  SampleCheckbox,
  SingleTableBoxScene,
} from "@/shared/components/ui";
import CustomCellphoneTextField from "@/shared/components/ui/CustomCellphoneTextField";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { IdentificacionTextField } from "@/erp/administration/empleados/shared/components/IdentificacionEmpleadosTextField";
import {
  useCreateEmpleado,
  useFetchCargos,
  useUpdateEmpleado,
} from "@/shared/actions/erp/empleados";
import {
  Area,
  CanalVenta,
  City,
  Country,
  Departamento,
  Empresa,
  IDataRuc,
  Province,
  TipoDocumentoNameEnum,
  Zone,
} from "@/shared/interfaces";
import { Cargos, Empleado } from "@/shared/interfaces/erp/empleados";
import { useUiStore } from "@/store/ui";
import { Grid } from "@mui/material";
const returnUrlEmpleados = "/nomina/empleados";

export interface SaveEmpleadosProps {
  titlePage: string;
  editEmpleados?: Empleado;
}

type SaveFormData = {
  tipo_identificacion: string;
  identificacion: string;
  numero_referencia: string;
  razon_social?: string;
  direccion: string;
  email: string;
  telefono_1: string;
  telefono_2: string;
  telefono_3: string;
  canal_venta: string;

  ///* fk
  tipo_empleado: string;
  pais: string;
  provincia: string;
  ciudad: string;
  parroquia?: string;
  zona?: string;
  sector?: string;
  empresa?: string;
  area: string;
  departamento: string;
  user?: string | null;
  cargo?: string;
  estado: boolean;
};
const SaveEmpleados: React.FC<SaveEmpleadosProps> = ({
  titlePage,
  editEmpleados,
}) => {
  const navigate = useNavigate();

  ///* local state
  const [shrink, setShrink] = useState(false);

  ///* global state
  const setAlert = useUiStore((s) => s.setAlert);
  const setIsGlobalLoading = useUiStore((s) => s.setIsGlobalLoading);

  ///* form
  const form = useForm<SaveFormData>({
    resolver: yupResolver(newEmpleadoschema),
    defaultValues: {
      estado: true!,
    },
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isValid },
  } = form;
  const watchedCountry = form.watch("pais");
  const watchedProvince = form.watch("provincia");
  const watchedCity = form.watch("ciudad");

  ///* fetch data
  const { data: countriesPaging, isLoading: isLoadingCountries } =
    useFetchAllCountries({
      page_size: 600,
    });
  const { data: provincesPaging, isLoading: isLoadingProvinces } =
    useFetchProvinces(
      {
        page_size: 600,
        pais: watchedCountry,
      },
      !!watchedCountry,
    );
  const { data: citiesPaging, isLoading: isLoadingCities } =
    useFetchCitiesEnabled({
      params: {
        page_size: 600,
        provincia: watchedProvince,
        pais: watchedCountry,
      },
      enabled: !!watchedProvince && !!watchedCountry,
    });
  const { data: zonesPaging, isLoading: isLoadingZones } = useFetchZones(
    {
      page_size: 600,
      ciudad: watchedCity,
    },
    !!watchedCity,
  );
  const { data: EmpresaPaging, isLoading: isLoadingEmpresa } = usefetchEmpresas(
    {
      page_size: 600,
    },
  );
  const { data: AreaPaging, isLoading: isLoadingArea } = useFetchAreas({
    page_size: 600,
  });
  const { data: DepartamentoPaging, isLoading: isLoadingDepartamento } =
    useFetchDepartamentos({
      page_size: 600,
    });
  const { data: CanalVentaPaging, isLoading: isLoadingCanalVenta } =
    useFetchCanalVentaAll({
      params: {
        page_size: 600,
      },
    });
  const { data: CargosPaging, isLoading: isLoadingCargos } = useFetchCargos({
    page_size: 600,
  });

  ///* mutations
  const createEmpleados = useCreateEmpleado({
    navigate,
    returnUrl: returnUrlEmpleados,
  });
  const updateEmpleados = useUpdateEmpleado({
    navigate,
    returnUrl: returnUrlEmpleados,
  });
  const watchedTipoIdentificacion = form.watch("tipo_identificacion");
  const consultaIdentificacion = useFetchSearchIdentification({
    customSuccessMessage: "Datos de identificación encontrados.",
  });

  ///* handlers

  const onSave = () => {
    if (!isValid) return;

    ///* upd
    if (editEmpleados?.id_empleado) {
      return updateEmpleados.mutate({
        id: editEmpleados.id_empleado,
        data: form.getValues() as any,
      });
    }

    ///* create
    return createEmpleados.mutate(form.getValues() as any);
  };

  const onChangeCiudad = (value: string) => {
    form.setValue("ciudad", value);
  };
  const onChangeProvince = (value: string) => {
    form.setValue("provincia", value);
  };

  const handleConsultaIdentificacion = async (value: string) => {
    setIsGlobalLoading(true);
    if (!value) {
      setIsGlobalLoading(false);
      form.setValue("razon_social", "");
      return;
    }

    if (watchedTipoIdentificacion === TipoDocumentoNameEnum.CEDULA) {
      const res = await consultaIdentificacion.mutateAsync({
        identificacion: value,
      });
      setIsGlobalLoading(false);
      const { data } = res;
      if (!data) return;

      form.setValue("razon_social", data?.razon_social);

      return;
    }

    if (watchedTipoIdentificacion === TipoDocumentoNameEnum.RUC) {
      const res = await consultaIdentificacion.mutateAsync({
        identificacion: value,
      });
      setIsGlobalLoading(false);

      const data: IDataRuc = res?.data as unknown as IDataRuc;

      form.setValue("razon_social", data?.razonSocial);

      return;
    }

    setIsGlobalLoading(false);
  };
  ///* effects
  useEffect(() => {
    if (editEmpleados?.id_empleado) {
      setShrink(true);
      reset({
        ...editEmpleados,
        estado: editEmpleados.estado ? ("1" as any) : ("0" as any),
      });
    }
  }, [editEmpleados, form]);

  const tipoIdentificacionOptions = Object.entries(TipoIdentificacionEnumChoice)
    // Filter since employees should only be accepted with cedula
    .filter(([key]) => key === "CEDULA")
    .map(([key, value]) => ({ label: key, value }));

  // Set default value if only one option exists
  useEffect(() => {
    if (tipoIdentificacionOptions.length === 1) {
      form.setValue("tipo_identificacion", tipoIdentificacionOptions[0].value);
    }
  }, [tipoIdentificacionOptions, form]);

  const tipoEmpleadosOptions = Object.entries(TipoEmpleadoEnumChoice).map(
    ([key, value]) => ({ label: key, value }),
  );
  //console.log("form", form)

  return (
    <SingleTableBoxScene
      title={titlePage}
      enableBackBtn={true}
      backPageUrl="/nomina/empleados"
      visibleReport={false}
      enableCreateOrEditBtn={true}
      textCreateOrEditBtn="Guardar"
      fnCreateOrEdit={handleSubmit(onSave, () => {
        setAlert(true, "Faltan campos por llenar", "error");
      })}
    >
      <Grid container spacing={4}>
        <CustomAutocomplete
          label="Tipo de Identificación"
          name="tipo_identificacion"
          control={control}
          defaultValue={form.getValues().tipo_identificacion || ""}
          error={errors.tipo_identificacion}
          helperText={errors.tipo_identificacion?.message}
          options={tipoIdentificacionOptions}
          isLoadingData={false}
          valueKey="value"
          optionLabelForEdit={form.getValues().tipo_identificacion}
          size={gridSizeMdLg4}
        />

        <IdentificacionTextField
          control={control}
          name="identificacion"
          label="Identificación"
          defaultValue={form.getValues().identificacion || ""}
          error={errors.identificacion}
          helperText={errors.identificacion?.message}
          disabled={!watchedTipoIdentificacion}
          selectedDocumentType={watchedTipoIdentificacion}
          onFetchCedulaRucInfo={handleConsultaIdentificacion}
          size={gridSizeMdLg4}
        />

        <CustomTextField
          name="numero_referencia"
          label="Referencia"
          control={control}
          defaultValue={form.getValues().numero_referencia || ""}
          error={errors.numero_referencia}
          helperText={errors.numero_referencia?.message}
          shrink={shrink}
          size={gridSizeMdLg4}
        />

        <CustomTextField
          name="razon_social"
          label="Razón Social"
          control={control}
          defaultValue={form.getValues().razon_social ?? ""}
          error={errors.razon_social}
          helperText={errors.razon_social?.message}
          shrink={shrink}
          size={gridSizeMdLg4}
        />

        <CustomTextField
          name="direccion"
          label="Dirección"
          control={control}
          defaultValue={form.getValues().direccion || ""}
          error={errors.direccion}
          helperText={errors.direccion?.message}
          shrink={shrink}
          size={gridSizeMdLg4}
        />

        <CustomTextField
          name="email"
          label="Correo Electrónico"
          control={control}
          defaultValue={form.getValues().email || ""}
          error={errors.email}
          helperText={errors.email?.message}
          shrink={shrink}
          type="email"
          size={gridSizeMdLg4}
        />

        <CustomCellphoneTextField
          name="telefono_1"
          label="Teléfono 1"
          control={control}
          defaultValue={form.getValues().telefono_1 || ""}
          error={errors.telefono_1}
          helperText={errors.telefono_1?.message}
          shrink={shrink}
          size={gridSizeMdLg4}
        />

        <CustomCellphoneTextField
          name="telefono_2"
          label="Teléfono 2"
          control={control}
          defaultValue={form.getValues().telefono_2 || ""}
          error={errors.telefono_2}
          helperText={errors.telefono_2?.message}
          shrink={shrink}
          size={gridSizeMdLg4}
        />
        <CustomCellphoneTextField
          name="telefono_3"
          label="Teléfono 3"
          control={control}
          defaultValue={form.getValues().telefono_3 || ""}
          error={errors.telefono_3}
          helperText={errors.telefono_3?.message}
          shrink={shrink}
          size={gridSizeMdLg4}
        />
        <CustomAutocomplete
          label="Tipo de empleado"
          name="tipo_empleado"
          control={form.control}
          defaultValue={form.getValues().tipo_empleado || ""}
          error={errors.tipo_empleado}
          helperText={errors.tipo_empleado?.message}
          options={tipoEmpleadosOptions}
          isLoadingData={false}
          valueKey="value"
          optionLabelForEdit={form.getValues().tipo_empleado}
          size={gridSizeMdLg4}
        />
        <CustomAutocomplete<Country>
          label="Pais"
          name="pais"
          actualValueKey="codigo"
          //// options
          options={countriesPaging?.data.data || []}
          // label & search by name
          valueKey="nombre"
          //
          defaultValue={form.getValues().pais || ""}
          optionLabelForEdit={form.getValues().pais || ""}
          isLoadingData={isLoadingCountries}
          //// validation
          control={control}
          error={errors.pais}
          helperText={errors.pais?.message}
          onChangeValue={(e) => {
            form.setValue("pais", e);
          }}
          size={gridSizeMdLg4}
        />
        <CustomAutocomplete<Province>
          label="Provincia"
          name="provincia"
          actualValueKey="codigo"
          //// options
          options={provincesPaging?.data.data || []}
          // label & search by name
          valueKey="nombre"
          //
          defaultValue={form.getValues().provincia || ""}
          optionLabelForEdit={form.getValues().provincia || ""}
          isLoadingData={isLoadingProvinces}
          //// validation
          control={control}
          error={errors.provincia}
          helperText={errors.provincia?.message}
          onChangeValue={onChangeProvince}
          size={gridSizeMdLg4}
        />
        <CustomAutocomplete<City>
          label="Ciudad"
          name="ciudad"
          actualValueKey="codigo"
          //// options
          options={citiesPaging?.data.data || []}
          // label & search by name
          valueKey="nombre"
          //
          defaultValue={form.getValues().ciudad || ""}
          optionLabelForEdit={form.getValues().ciudad || ""}
          isLoadingData={isLoadingCities}
          //// validation
          control={control}
          error={errors.ciudad}
          helperText={errors.ciudad?.message}
          onChangeValue={onChangeCiudad}
          size={gridSizeMdLg4}
        />
        <CustomAutocomplete<Zone>
          label="Zona"
          name="zona"
          actualValueKey="codigo"
          //// options
          options={zonesPaging?.data.data || []}
          // label & search by name
          valueKey="nombre"
          //
          defaultValue={form.getValues().zona ?? ""}
          optionLabelForEdit={form.getValues().zona ?? ""}
          isLoadingData={isLoadingZones}
          //// validation
          control={control}
          error={errors.zona}
          helperText={errors.zona?.message}
          onChangeValue={(e) => {
            form.setValue("zona", e);
          }}
          size={gridSizeMdLg4}
        />

        <CustomAutocomplete<Empresa>
          label="Empresa"
          name="empresa"
          actualValueKey="codigo"
          //// options
          options={EmpresaPaging?.data.data || []}
          // label & search by name
          valueKey="razon_social"
          //
          defaultValue={form.getValues().empresa ?? ""}
          optionLabelForEdit={form.getValues().empresa ?? ""}
          isLoadingData={isLoadingEmpresa}
          //// validation
          control={control}
          error={errors.empresa}
          helperText={errors.empresa?.message}
          onChangeValue={(e) => {
            form.setValue("empresa", e);
          }}
          size={gridSizeMdLg4}
        />

        <CustomAutocomplete<Area>
          label="Area"
          name="area"
          actualValueKey="codigo"
          //// options
          options={AreaPaging?.data.data || []}
          // label & search by name
          valueKey="nombre"
          //
          defaultValue={form.getValues().area || ""}
          optionLabelForEdit={form.getValues().area || ""}
          isLoadingData={isLoadingArea}
          //// validation
          control={control}
          error={errors.area}
          helperText={errors.area?.message}
          onChangeValue={(e) => {
            form.setValue("area", e);
          }}
          size={gridSizeMdLg4}
        />
        <CustomAutocomplete<Departamento>
          label="Departamento"
          name="departamento"
          actualValueKey="codigo"
          //// options
          options={DepartamentoPaging?.data.data || []}
          // label & search by name
          valueKey="nombre"
          //
          defaultValue={form.getValues().departamento || ""}
          optionLabelForEdit={form.getValues().departamento || ""}
          isLoadingData={isLoadingDepartamento}
          //// validation
          control={control}
          error={errors.departamento}
          helperText={errors.departamento?.message}
          onChangeValue={(e) => {
            form.setValue("departamento", e);
          }}
          size={gridSizeMdLg4}
        />
        <CustomAutocomplete<CanalVenta>
          label="Canal venta"
          name="canal_venta"
          actualValueKey="codigo"
          //// options
          options={CanalVentaPaging?.data.data || []}
          // label & search by name
          valueKey="nombre"
          //
          defaultValue={form.getValues().canal_venta || ""}
          optionLabelForEdit={form.getValues().canal_venta || ""}
          isLoadingData={isLoadingCanalVenta}
          //// validation
          control={control}
          error={errors.canal_venta}
          helperText={errors.canal_venta?.message}
          onChangeValue={(e) => {
            form.setValue("canal_venta", e);
          }}
          size={gridSizeMdLg4}
        />
        <CustomAutocomplete<Cargos>
          label="Cargo"
          name="cargo"
          actualValueKey="codigo"
          //// options
          options={CargosPaging?.data.data || []}
          // label & search by name
          valueKey="nombre"
          //
          defaultValue={form.getValues().cargo ?? ""}
          optionLabelForEdit={form.getValues().cargo ?? ""}
          isLoadingData={isLoadingCargos}
          //// validation
          control={control}
          error={errors.cargo}
          helperText={errors.cargo?.message}
          onChangeValue={(e) => {
            form.setValue("cargo", e);
          }}
          size={gridSizeMdLg4}
        />

        <SampleCheckbox
          label="Estado"
          name="estado"
          control={form.control}
          defaultValue={!!form.getValues().estado}
          isState
          size={gridSizeMdLg4}
        />
      </Grid>
    </SingleTableBoxScene>
  );
};
export default SaveEmpleados;
